import { useCallback, useEffect, useRef } from 'react';

/**
 * Coalesces callbacks to one invocation per animation frame.
 * Used to batch live dashboard graph publishes without dropping ingested samples.
 */
export function useLiveGraphPublish() {
  const rafIdRef = useRef<number | null>(null);
  const publishRef = useRef<(() => void) | null>(null);

  const cancelScheduledPublish = useCallback(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    publishRef.current = null;
  }, []);

  const flushPublish = useCallback(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    const publish = publishRef.current;
    publishRef.current = null;
    publish?.();
  }, []);

  const schedulePublish = useCallback((publish: () => void) => {
    publishRef.current = publish;
    if (rafIdRef.current != null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const nextPublish = publishRef.current;
      publishRef.current = null;
      nextPublish?.();
    });
  }, []);

  useEffect(() => cancelScheduledPublish, [cancelScheduledPublish]);

  return { schedulePublish, flushPublish, cancelScheduledPublish };
}
